import { X } from "lucide-react";
import { Dialog as D } from "radix-ui";
import type { KeyboardEvent, ReactNode } from "react";
import { cn } from "../lib/cn";
import { IconButton } from "./IconButton";

export interface DialogProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Element that opens the dialog. Optional when the dialog is controlled. */
  trigger?: ReactNode;
  title: ReactNode;
  /** One sentence. Leave it out when the title says enough. */
  description?: ReactNode;
  children?: ReactNode;
  /** Buttons, right aligned. Put the confirming action last. */
  footer?: ReactNode;
  /** xl is a large fixed-height sheet whose body fills it (a table that scrolls inside). */
  size?: "sm" | "md" | "lg" | "xl";
  /** Destructive dialogs get a danger hairline at the top edge. */
  tone?: "default" | "danger";
  className?: string;
  /** Keys pressed anywhere inside the dialog, before app-wide shortcuts see them. */
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  /** Keys released anywhere inside the dialog. */
  onKeyUp?: (e: KeyboardEvent<HTMLDivElement>) => void;
  /** Esc, before the dialog closes. preventDefault keeps it open (to cancel an inline edit). */
  onEscapeKeyDown?: (e: globalThis.KeyboardEvent) => void;
  /** Focus moving back to the trigger on close. preventDefault leaves focus where the next screen puts it. */
  onCloseAutoFocus?: (e: Event) => void;
  /** Focus moving into the dialog on open. preventDefault and focus a control to start there. */
  onOpenAutoFocus?: (e: Event) => void;
  /** Classes for the body (the children's wrapper). */
  bodyClassName?: string;
}

const widths = {
  sm: "max-w-[400px]",
  md: "max-w-[520px]",
  lg: "max-w-[720px]",
  xl: "h-[calc(100vh-48px)] max-h-[880px] max-w-[1180px]",
} as const;

/** Modal dialog. Esc, the close button and a scrim click all dismiss it. */
export function Dialog({
  open,
  defaultOpen,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  size = "md",
  tone = "default",
  className,
  onKeyDown,
  onKeyUp,
  onEscapeKeyDown,
  onCloseAutoFocus,
  onOpenAutoFocus,
  bodyClassName,
}: DialogProps) {
  return (
    <D.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      {trigger ? <D.Trigger asChild>{trigger}</D.Trigger> : null}
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 bg-scrim data-[state=closed]:animate-vd-fade-out data-[state=open]:animate-vd-fade-in" />
        <D.Content
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          {...(onEscapeKeyDown ? { onEscapeKeyDown } : {})}
          {...(onCloseAutoFocus ? { onCloseAutoFocus } : {})}
          {...(onOpenAutoFocus ? { onOpenAutoFocus } : {})}
          className={cn(
            "fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100vh-48px)] w-[calc(100vw-32px)] -translate-x-1/2 -translate-y-1/2 flex-col",
            "rounded-xl bg-overlay text-ink shadow-dialog outline-none",
            "data-[state=closed]:animate-vd-pop-out data-[state=open]:animate-vd-pop-in",
            tone === "danger" &&
              "shadow-[var(--vd-shadow-dialog),inset_0_2px_0_0_var(--vd-danger)]",
            widths[size],
            className,
          )}
        >
          <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-3">
            <div className="flex flex-col gap-1">
              <D.Title className="font-display font-medium text-heading">{title}</D.Title>
              {description ? (
                <D.Description className="text-body text-ink-2">{description}</D.Description>
              ) : (
                <D.Description className="sr-only">{title}</D.Description>
              )}
            </div>
            <D.Close asChild>
              <IconButton label="Close" icon={<X />} size="sm" tooltip={false} className="-mr-1" />
            </D.Close>
          </div>
          {children ? (
            <div
              className={cn(
                "min-h-0 px-5 pb-4",
                size === "xl" ? "flex flex-1 flex-col overflow-hidden" : "overflow-y-auto",
                bodyClassName,
              )}
            >
              {children}
            </div>
          ) : null}
          {footer ? (
            <div className="flex items-center justify-end gap-2 border-hairline border-t px-5 py-3.5">
              {footer}
            </div>
          ) : null}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

export const DialogClose = D.Close;
